import { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import Select from "react-select";
import FeatherIcon from "feather-icons-react";
import {
  getPayrollCycles,
  seedPayrollCycles,
  formatCurrency,
} from "./Api";

const BLUE = "#2563EB";
const NAVY = "#1E3A8A";

// Status color language (see legend): green = all good (Paid / Approved),
// purple = pending approval, red = declined / overdue, amber = current cycle.
const COLORS = {
  green:  { text: "#047857", bg: "#ECFDF5", border: "#A7F3D0" },
  purple: { text: "#6D28D9", bg: "#F5F3FF", border: "#DDD6FE" },
  red:    { text: "#DC2626", bg: "#FEF2F2", border: "#FECACA" },
  amber:  { text: "#B45309", bg: "#FFFBEB", border: "#FDE68A" },
  gray:   { text: "#64748B", bg: "#F1F5F9", border: "#E2E8F0" },
};

const PAGE_SIZE = 6;

const selectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    borderColor: isFocused ? BLUE : "#E2E8F0",
    borderRadius: 8,
    minHeight: 34,
    fontSize: "0.8rem",
    boxShadow: isFocused ? "0 0 0 2px rgba(37,99,235,0.12)" : "none",
    minWidth: 130,
    "&:hover": { borderColor: BLUE },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.8rem",
    background: isSelected ? BLUE : isFocused ? "#EFF6FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: "1px solid #E2E8F0", zIndex: 99999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  ...["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(
    (m, i) => ({ value: String(i + 1), label: m })
  ),
];

const nowYear = new Date().getFullYear();
const YEAR_OPTIONS = [
  { value: "", label: "All Years" },
  ...Array.from({ length: 4 }, (_, i) => {
    const y = nowYear - 1 + i;
    return { value: String(y), label: String(y) };
  }),
];

const STATUS_OPTIONS = [
  { value: "", label: "All Cycles" },
  { value: "upcoming", label: "Upcoming" },
  { value: "completed", label: "Completed" },
];

const parseDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (s) => {
  const dt = parseDate(s);
  return dt ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
};

const fmtWeekday = (s) => {
  const dt = parseDate(s);
  return dt ? dt.toLocaleDateString("en-US", { weekday: "long" }) : "";
};

const monthLabel = (s) => {
  const dt = parseDate(s);
  return dt ? dt.toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "";
};

const daysFromToday = (s) => {
  const dt = parseDate(s);
  if (!dt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((dt - today) / 86400000);
};

const countdownLabel = (s) => {
  const d = daysFromToday(s);
  if (d == null) return "";
  if (d > 1) return `in ${d} days`;
  if (d === 1) return "tomorrow";
  if (d === 0) return "today";
  return "passed";
};

const TH_STYLE = {
  textAlign: "left", padding: "10px 14px", fontSize: "0.7rem", fontWeight: 800,
  color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px",
  borderBottom: "2px solid #E7EAF0", background: "#F8FAFC", whiteSpace: "nowrap",
};

const TD_STYLE = {
  padding: "12px 14px", fontSize: "0.83rem", color: "#0F172A",
  borderBottom: "1px solid #EEF2F7", verticalAlign: "middle",
};

const Chip = ({ color, icon, children, title }) => {
  const c = COLORS[color];
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: "2px 9px", fontSize: "0.7rem", fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      <FeatherIcon icon={icon} size={10} />
      {children}
    </span>
  );
};

const DateCell = ({ date, muted }) => (
  <div>
    <div style={{ fontWeight: 700, color: muted ? "#94A3B8" : "#0F172A", whiteSpace: "nowrap" }}>{fmtDate(date)}</div>
    <div style={{ fontSize: "0.7rem", color: muted ? "#CBD5E1" : "#64748B", marginTop: 1 }}>{fmtWeekday(date)}</div>
  </div>
);

const LegendDot = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.74rem", color: "#475569", fontWeight: 600 }}>
    <span style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS[color].text, flexShrink: 0 }} />
    {label}
  </span>
);

const PayrollCalendarModal = ({ isOpen, onClose, userRole = "" }) => {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, today: "", nextCycle: null, hasAnyCycles: true, canManage: false });
  const [filters, setFilters] = useState({ year: "", month: "", status: "" });
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const isCompany = (userRole || "").toLowerCase() === "company" || meta.canManage;

  const fetchCycles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;
      if (filters.status) params.status = filters.status;
      const res = await getPayrollCycles(params);
      setCycles(res?.data || []);
      setMeta({
        total: res?.total || 0,
        totalPages: res?.totalPages || 1,
        today: res?.today || "",
        nextCycle: res?.nextCycle || null,
        hasAnyCycles: !!res?.hasAnyCycles,
        canManage: !!res?.canManage,
      });
    } catch {
      setCycles([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    if (isOpen) fetchCycles();
  }, [isOpen, fetchCycles]);

  useEffect(() => {
    if (!isOpen) {
      setPage(1);
      setFilters({ year: "", month: "", status: "" });
    }
  }, [isOpen]);

  const setFilter = (k) => (opt) => {
    setPage(1);
    setFilters((f) => ({ ...f, [k]: opt?.value || "" }));
  };

  // Lifecycle of a cycle (time-based): current (amber) / upcoming / completed —
  // used only for row emphasis, not shown as its own column.
  const lifecycleOf = (c) => {
    if (meta.nextCycle && String(c._id) === String(meta.nextCycle._id)) return "current";
    if (meta.today && c.paymentDate < meta.today) return "completed";
    return "upcoming";
  };

  const loadDefaults = async () => {
    setBusy(true);
    try {
      const res = await seedPayrollCycles();
      fetchCycles();
      return res;
    } catch {
      // handled in api helper
    } finally {
      setBusy(false);
    }
  };

  const statChips = (c) => {
    const counts = c.stats?.counts || {};
    const totals = c.stats?.totals || {};
    const chips = [];
    if (counts.Paid) chips.push(
      <Chip key="paid" color="green" icon="check-circle" title={`Paid total: ${formatCurrency(totals.Paid || 0)}`}>
        {counts.Paid} Paid
      </Chip>
    );
    if (counts.Approved) chips.push(
      <Chip key="approved" color="green" icon="check" title={`Approved total: ${formatCurrency(totals.Approved || 0)}`}>
        {counts.Approved} Approved
      </Chip>
    );
    if (counts.Pending) chips.push(
      <Chip key="pending" color="purple" icon="clock" title={`Pending total: ${formatCurrency(totals.Pending || 0)}`}>
        {counts.Pending} Pending
      </Chip>
    );
    if (counts.Declined) chips.push(
      <Chip key="declined" color="red" icon="x-circle" title={`Declined total: ${formatCurrency(totals.Declined || 0)}`}>
        {counts.Declined} Declined
      </Chip>
    );
    if (counts.Overdue) chips.push(
      <Chip key="overdue" color="red" icon="alert-circle" title={`Overdue total: ${formatCurrency(totals.Overdue || 0)}`}>
        {counts.Overdue} Overdue
      </Chip>
    );
    return chips;
  };

  const from = meta.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, meta.total);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered
      contentClassName="iv-form-modal-content">
      <ModalHeader toggle={onClose} style={{
        borderBottom: "1px solid #EEF2F7", padding: "16px 24px 14px",
        background: "#fff", borderRadius: "14px 14px 0 0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
            background: BLUE,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(37,99,235,0.3)",
          }}>
            <FeatherIcon icon="calendar" size={20} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>
              Payments Calendar
            </div>
            <div style={{ fontSize: "0.78rem", color: "#64748B", fontWeight: 500, marginTop: 3 }}>
              Monthly payables schedule — invoice submission, approval &amp; payment processing
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: "20px 26px", background: "#fff" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Next cycle highlight */}
          {meta.nextCycle && (
            <div style={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 18,
              background: NAVY, borderRadius: 12, borderLeft: "5px solid #F59E0B",
              padding: "14px 22px",
            }}>
              <div style={{ marginRight: "auto" }}>
                <div style={{ fontSize: "0.68rem", fontWeight: 800, color: "#FCD34D", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                  Next Payment Cycle
                </div>
                <div style={{ fontSize: "1.02rem", fontWeight: 800, color: "#fff", marginTop: 3 }}>
                  {monthLabel(meta.nextCycle.paymentDate)}
                </div>
              </div>
              {[
                { label: "Submit By", date: meta.nextCycle.submitBy },
                { label: "Approve By", date: meta.nextCycle.approveBy },
                { label: "Payment", date: meta.nextCycle.paymentDate },
              ].map((b) => (
                <div key={b.label} style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.66rem", fontWeight: 700, color: "#BFDBFE", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {b.label}
                  </div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#fff", marginTop: 2 }}>
                    {fmtDate(b.date)}
                  </div>
                  <span style={{
                    display: "inline-block", marginTop: 4,
                    background: "rgba(255,255,255,0.14)", color: "#FDE68A",
                    borderRadius: 20, padding: "1px 8px", fontSize: "0.66rem", fontWeight: 700,
                  }}>
                    {countdownLabel(b.date)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <FeatherIcon icon="filter" size={14} color="#64748B" />
            <Select
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find((o) => o.value === filters.year) || YEAR_OPTIONS[0]}
              onChange={setFilter("year")}
              styles={selectStyles}
              menuPortalTarget={document.body} menuPosition="fixed"
            />
            <Select
              options={MONTH_OPTIONS}
              value={MONTH_OPTIONS.find((o) => o.value === filters.month) || MONTH_OPTIONS[0]}
              onChange={setFilter("month")}
              styles={selectStyles}
              menuPortalTarget={document.body} menuPosition="fixed"
            />
            <Select
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === filters.status) || STATUS_OPTIONS[0]}
              onChange={setFilter("status")}
              styles={selectStyles}
              menuPortalTarget={document.body} menuPosition="fixed"
            />
            {(filters.year || filters.month || filters.status) && (
              <button
                type="button"
                onClick={() => { setPage(1); setFilters({ year: "", month: "", status: "" }); }}
                style={{
                  background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 7,
                  color: "#DC2626", fontSize: "0.74rem", fontWeight: 600,
                  padding: "5px 11px", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}
              >
                <FeatherIcon icon="x" size={11} /> Clear
              </button>
            )}
          </div>

          {/* Color legend */}
          <div style={{
            display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
            background: "#F8FAFC", border: "1px solid #E7EAF0", borderRadius: 10,
            padding: "9px 14px",
          }}>
            <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Legend
            </span>
            <LegendDot color="green" label="All good — Paid / Approved" />
            <LegendDot color="purple" label="Pending approval" />
            <LegendDot color="red" label="Declined / Overdue" />
            <LegendDot color="amber" label="Current cycle" />
          </div>

          {/* Schedule table (read-only) */}
          <div style={{ border: "1px solid #E7EAF0", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={TH_STYLE}>Month</th>
                    <th style={TH_STYLE}>Submit By</th>
                    <th style={TH_STYLE}>Approve By</th>
                    <th style={TH_STYLE}>Payment Date</th>
                    <th style={TH_STYLE}>Invoices In Cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "40px 0" }}>
                        <Spinner style={{ color: BLUE }} />
                      </td>
                    </tr>
                  )}

                  {!loading && cycles.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "40px 20px" }}>
                        <FeatherIcon icon="calendar" size={26} color="#94A3B8" />
                        <div style={{ fontWeight: 700, color: "#0F172A", marginTop: 8, fontSize: "0.9rem" }}>
                          {meta.hasAnyCycles ? "No cycles match the current filters" : "No payments calendar yet"}
                        </div>
                        <div style={{ color: "#64748B", fontSize: "0.78rem", marginTop: 4 }}>
                          {meta.hasAnyCycles
                            ? "Try clearing the filters above."
                            : isCompany
                              ? "Load the published schedule to get started."
                              : "Your company admin has not published the payables schedule yet."}
                        </div>
                        {!meta.hasAnyCycles && isCompany && (
                          <button type="button" onClick={loadDefaults} disabled={busy}
                            style={{
                              marginTop: 12, background: BLUE, border: "none", color: "#fff",
                              borderRadius: 8, padding: "9px 16px", fontSize: "0.78rem", fontWeight: 700,
                              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                            }}>
                            {busy ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : <FeatherIcon icon="download" size={13} />}
                            Load Published Schedule (Aug–Dec 2026)
                          </button>
                        )}
                      </td>
                    </tr>
                  )}

                  {!loading && cycles.map((c) => {
                    const lc = lifecycleOf(c);
                    const muted = lc === "completed";
                    const chips = statChips(c);
                    return (
                      <tr key={c._id} style={{ background: lc === "current" ? "#FFFBEB" : "transparent" }}>
                        <td style={{ ...TD_STYLE, fontWeight: 800, color: muted ? "#94A3B8" : "#0F172A", whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <FeatherIcon icon="calendar" size={14} color={lc === "current" ? "#F59E0B" : muted ? "#CBD5E1" : BLUE} />
                            {monthLabel(c.paymentDate)}
                          </div>
                          {c.notes && (
                            <div style={{ fontSize: "0.68rem", color: "#94A3B8", fontWeight: 500, marginTop: 2 }}>
                              {c.notes}
                            </div>
                          )}
                        </td>
                        <td style={TD_STYLE}><DateCell date={c.submitBy} muted={muted} /></td>
                        <td style={TD_STYLE}><DateCell date={c.approveBy} muted={muted} /></td>
                        <td style={TD_STYLE}><DateCell date={c.paymentDate} muted={muted} /></td>
                        <td style={TD_STYLE}>
                          {chips.length ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                              {chips}
                              <span style={{ fontSize: "0.7rem", color: "#64748B", fontWeight: 700, marginLeft: 2 }}>
                                {formatCurrency(c.stats?.amountTotal || 0)}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: "0.74rem", color: "#94A3B8" }}>No invoices</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta.total > 0 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px", background: "#F8FAFC", borderTop: "1px solid #E7EAF0",
                flexWrap: "wrap", gap: 8,
              }}>
                <span style={{ fontSize: "0.74rem", color: "#64748B", fontWeight: 600 }}>
                  Showing {from}–{to} of {meta.total} cycle{meta.total !== 1 ? "s" : ""}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
                    style={{
                      background: "#fff", border: "1px solid #E2E8F0", color: page <= 1 ? "#CBD5E1" : "#334155",
                      borderRadius: 7, padding: "5px 10px", fontSize: "0.74rem", fontWeight: 700,
                      cursor: page <= 1 ? "not-allowed" : "pointer",
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                    <FeatherIcon icon="chevron-left" size={12} /> Prev
                  </button>
                  <span style={{ fontSize: "0.74rem", color: "#334155", fontWeight: 700 }}>
                    Page {page} of {meta.totalPages}
                  </span>
                  <button type="button" disabled={page >= meta.totalPages || loading} onClick={() => setPage((p) => p + 1)}
                    style={{
                      background: "#fff", border: "1px solid #E2E8F0", color: page >= meta.totalPages ? "#CBD5E1" : "#334155",
                      borderRadius: 7, padding: "5px 10px", fontSize: "0.74rem", fontWeight: 700,
                      cursor: page >= meta.totalPages ? "not-allowed" : "pointer",
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}>
                    Next <FeatherIcon icon="chevron-right" size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Rules */}
          <div style={{
            display: "flex", gap: 12, alignItems: "flex-start",
            background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12,
            padding: "12px 16px",
          }}>
            <span style={{
              width: 34, height: 34, borderRadius: 9, background: "#FEF3C7", flexShrink: 0,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>
              <FeatherIcon icon="info" size={15} color="#D97706" />
            </span>
            <div style={{ fontSize: "0.78rem", color: "#92400E", lineHeight: 1.55 }}>
              <strong>How these dates are set:</strong> submission dates are 2 business days prior to the
              payment date, with approvals done 1 business day prior to payment. Any payable day that
              lands on a Monday or holiday is pushed to the following business day to allow for the
              approval process. Invoices submitted after a Submit By date roll into the next cycle.
            </div>
          </div>

        </div>
      </ModalBody>

      <ModalFooter style={{
        borderTop: "1px solid #E9EEF5", padding: "12px 24px",
        background: "#F8FAFC", borderRadius: "0 0 14px 14px",
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            height: 42, fontWeight: 600, fontSize: "0.85rem", borderRadius: 10,
            padding: "0 22px", background: "#fff", border: "1px solid #E2E8F0", color: "#334155",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
};

export { PayrollCalendarModal };
